import React from 'react'
import { FilterProps } from '../../shared/shared.interface'
import { Column, WrapRow, RatioContainer } from '../../shared/shared.styled'
import styled from 'styled-components'
import SearchBar from '../../shared/components/search-bar.component'
import Checkbox from '../../shared/components/checkbox.component'
import Dropdown from '../../shared/components/dropdown.component'
import { DropdownUtils } from '../../shared/utils/dropdowns.utils'
import { useSelector } from 'react-redux'
import { IStoreInterface } from '../../configs/store.config'
import TextInput from '../../shared/components/text-input.component'
import { useIsAdmin } from '../../shared/hooks/use-is-admin.hook'
import { useDebounce } from '../../shared/hooks/use-debounce.hook'
import { VehicleFilters } from './vehicle-search.component'
import SmallButton from '../../shared/components/small-button.component'
import CloseIcon from '../../assets/icons/close.icon'
import { Stand } from '../../shared/shared.models'
import { AdminApi } from '../admin.api'
import { useAlert } from 'react-alert'
import { Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export const SoldVehiclesSearch = ({
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
}: FilterProps) => {
    const isAdmin = useIsAdmin()
    // Redux state
    const users = useSelector(
        (state: IStoreInterface) => state.adminReducer.users
    )
    const financials = useSelector(
        (state: IStoreInterface) => state.adminReducer.financials
    )

    const alert = useAlert()
    const [stands, setStands] = React.useState<Stand[]>([])
    const { company } = useSelector(
        (state: IStoreInterface) => state.adminReducer
    )

    // Local state for text inputs to enable debouncing
    const [brandOrModel, setBrandOrModel] = React.useState('')
    const [licensePlate, setLicensePlate] = React.useState('')
    const [saleMonth, setSaleMonth] = React.useState(
        filters[VehicleFilters.SALE_MONTH] || ''
    )
    const [saleYear, setSaleYear] = React.useState(
        filters[VehicleFilters.SALE_YEAR] || ''
    )

    const currentYear = new Date().getFullYear()
    const minYear = 2010

    // Keep track of last valid year so we can revert invalid edits
    const prevValidYear = React.useRef<number>(
        filters[VehicleFilters.SALE_YEAR]
            ? Number(filters[VehicleFilters.SALE_YEAR])
            : currentYear
    )

    // Update local state when filters prop changes
    React.useEffect(() => {
        setSaleMonth(filters[VehicleFilters.SALE_MONTH] || '')
        setSaleYear(filters[VehicleFilters.SALE_YEAR] || '')
    }, [filters])

    // Track if component has mounted to prevent initial debounce triggers
    const hasMounted = React.useRef(false)

    // Debounced values for text inputs (1 second delay)
    const debouncedBrandOrModel = useDebounce(brandOrModel, 1000)
    const debouncedLicensePlate = useDebounce(licensePlate, 1000)
    const debouncedSaleMonth = useDebounce(saleMonth, 1000)
    const debouncedSaleYear = useDebounce(saleYear, 2000)

    React.useEffect(() => {
        try {
            if (company) {
                AdminApi.methods.getStands(company.uuid).then(
                    (res) => {
                        setStands(res.data.stands)
                    },
                    (e) => {
                        alert.error(
                            'Erro ao listar os stands. Por favor tente mais tarde'
                        )
                    }
                )
            }
        } catch (e) {
            alert.error('Erro ao listar os stands. Por favor tente mais tarde')
        }
    }, [alert, company])

    // Function to update URL and filters
    const updateFilterAndUrl = React.useCallback(
        (filterKey: any, filterValue: any) => {
            // Special handling for sale year: validate and block invalid values
            if (filterKey === VehicleFilters.SALE_YEAR) {
                const yearStr = String(filterValue)
                const yearNum = Number(yearStr)
                if (!yearStr || isNaN(yearNum)) {
                    alert.error(
                        `O ano não pode ser vazio. Valores válidos: ${minYear}–${currentYear}`
                    )
                    // revert the visible input to the last valid year
                    setSaleYear(prevValidYear.current.toString())
                    return
                }

                if (yearNum < minYear || yearNum > currentYear) {
                    alert.error(
                        `Ano inválido. Utilize valores entre ${minYear} e ${currentYear}`
                    )
                    setSaleYear(prevValidYear.current.toString())
                    return
                }

                // accept and remember
                prevValidYear.current = yearNum
            }

            // Update filter in state
            updateFilter(filterKey, filterValue)

            // Construct new URL with filter parameter
            const queryParams = new URLSearchParams(window.location.search)
            if (filterValue?.length === 0) {
                queryParams.delete(filterKey)
            } else {
                queryParams.set(filterKey, filterValue)
            }

            // Update URL without reloading the page
            window.history.replaceState(
                {},
                '',
                `${window.location.pathname}?${queryParams.toString()}`
            )
        },
        [updateFilter]
    )

    // Update filters when debounced values change
    React.useEffect(() => {
        if (!hasMounted.current) return
        updateFilterAndUrl(VehicleFilters.BRAND_OR_MODEL, debouncedBrandOrModel)
    }, [debouncedBrandOrModel, updateFilterAndUrl])

    React.useEffect(() => {
        if (!hasMounted.current) return
        updateFilterAndUrl(VehicleFilters.LICENSE_PLATE, debouncedLicensePlate)
    }, [debouncedLicensePlate, updateFilterAndUrl])

    React.useEffect(() => {
        if (!hasMounted.current) return
        updateFilterAndUrl(VehicleFilters.SALE_MONTH, debouncedSaleMonth)
    }, [debouncedSaleMonth, updateFilterAndUrl])

    React.useEffect(() => {
        if (!hasMounted.current) return
        updateFilterAndUrl(VehicleFilters.SALE_YEAR, debouncedSaleYear)
    }, [debouncedSaleYear, updateFilterAndUrl])

    // Mark component as mounted after first render
    React.useEffect(() => {
        hasMounted.current = true
    }, [])

    // Handler for immediate filter changes (dropdowns, checkboxes)
    const handleFilterChange = React.useCallback(
        (filterKey: any, filterValue: any) => {
            updateFilterAndUrl(filterKey, filterValue)
        },
        [updateFilterAndUrl]
    )

    return (
        <SearchContainer>
            <WrapRow>
                <RatioContainer ratio={2 / 10}>
                    <SearchBar
                        handleChange={setBrandOrModel}
                        value={brandOrModel}
                        placeholder="Marca ou Modelo"
                    />
                </RatioContainer>
                <RatioContainer ratio={1 / 10}>
                    <SearchBar
                        handleChange={setLicensePlate}
                        value={licensePlate}
                        placeholder="Matrícula"
                    />
                </RatioContainer>
                <RatioContainer ratio={1 / 10}>
                    <TextInput
                        onChange={(e) => setSaleMonth(e.target.value)}
                        value={saleMonth.toString()}
                        placeholder="Mês de venda"
                        type="number"
                    />
                </RatioContainer>
                <RatioContainer ratio={1 / 10}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextInput
                            onChange={(e) => setSaleYear(e.target.value)}
                            value={saleYear.toString()}
                            placeholder="Ano de venda"
                            type="number"
                            min={minYear}
                            max={currentYear}
                        />
                        <Tooltip
                            title={`O ano não pode ser vazio. Valores: ${minYear}–${currentYear}`}
                        >
                            <InfoIconWrapper>
                                <InfoOutlinedIcon fontSize="small" />
                            </InfoIconWrapper>
                        </Tooltip>
                    </div>
                </RatioContainer>
                {isAdmin ? (
                    <>
                        <RatioContainer ratio={2 / 10}>
                            <Checkbox
                                label="Viaturas financiadas"
                                value={filters[VehicleFilters.FINANCED_VEHICLE]}
                                onChange={(e) =>
                                    handleFilterChange(
                                        VehicleFilters.FINANCED_VEHICLE,
                                        e.target.checked || false
                                    )
                                }
                            />
                        </RatioContainer>
                        <RatioContainer ratio={1 / 10}>
                            <Dropdown
                                selectValue={(val) =>
                                    handleFilterChange(
                                        VehicleFilters.LOAN_COMPANY,
                                        val.key
                                    )
                                }
                                value={filters[VehicleFilters.LOAN_COMPANY]}
                                clearValue={() =>
                                    handleFilterChange(
                                        VehicleFilters.LOAN_COMPANY,
                                        ''
                                    )
                                }
                                placeholder="Escolha a financeira"
                                options={DropdownUtils.getLoanCompanyDropdownOptions(
                                    financials ? financials : []
                                )}
                            />
                        </RatioContainer>
                        <RatioContainer ratio={1 / 10}>
                            <Dropdown
                                selectValue={(val) =>
                                    handleFilterChange(
                                        VehicleFilters.SELLER,
                                        val.key
                                    )
                                }
                                value={filters[VehicleFilters.SELLER]}
                                clearValue={() =>
                                    handleFilterChange(
                                        VehicleFilters.SELLER,
                                        ''
                                    )
                                }
                                placeholder="Escolha o vendedor"
                                options={DropdownUtils.getUsersDropdownOptions(
                                    users
                                )}
                            />
                        </RatioContainer>
                        <RatioContainer ratio={1 / 10}>
                            <Dropdown
                                selectValue={(val) =>
                                    handleFilterChange(
                                        VehicleFilters.STAND_LOCATION,
                                        val.key
                                    )
                                }
                                value={filters[VehicleFilters.STAND_LOCATION]}
                                clearValue={() =>
                                    handleFilterChange(
                                        VehicleFilters.STAND_LOCATION,
                                        ''
                                    )
                                }
                                placeholder="Escolha o stand"
                                options={DropdownUtils.getStandLocationDropdownOptions(
                                    stands
                                )}
                            />
                        </RatioContainer>
                    </>
                ) : null}
                <RatioContainer ratio={1.5 / 5}>
                    {Object.keys(filters).length > 0 ? (
                        <SmallButton
                            iconComponent={<CloseIcon />}
                            label="Limpar filtros"
                            onClick={() => {
                                const year = new Date().getFullYear()
                                clearAllFilters()
                                setBrandOrModel('')
                                setLicensePlate('')
                                setSaleMonth('')
                                setSaleYear(year.toString())
                                // ensure the sale year filter remains set after clearing
                                updateFilterAndUrl(
                                    VehicleFilters.SALE_YEAR,
                                    year
                                )
                            }}
                        />
                    ) : null}
                </RatioContainer>
            </WrapRow>
        </SearchContainer>
    )
}

const SearchContainer = styled(Column as any)`
    ${WrapRow} {
        > div {
            margin-bottom: 36px;
        }
        > div:not(:last-child) {
            margin-right: 24px;
        }
    }
`

const InfoIconWrapper = styled('span')`
    display: inline-flex;
    margin-left: 8px;
    color: #6b7280;
    align-items: center;
    svg {
        font-size: 16px;
    }
`
